import { DASH } from './format';

export interface TableFilterState {
    search: string;
    /** Chave da coluna filtrada. Vazio = qualquer coluna. */
    column: string;
    /** Valor exato (ja formatado) da coluna. Vazio = todos. */
    value: string;
}

export const EMPTY_FILTER: TableFilterState = { search: '', column: '', value: '' };

export type Row = Record<string, unknown>;
export type CellFormatter = (key: string, value: unknown) => string;

/** Minusculas e sem acento, para a busca nao depender de como a pessoa digita. */
export function normalizeText(s: string): string {
    return s
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
}

export function isFilterActive(f: TableFilterState): boolean {
    return f.search.trim() !== '' || f.column !== '' || f.value !== '';
}

/** Valores distintos de uma coluna como aparecem na tabela, em ordem alfabetica (traco por ultimo). */
export function distinctValues(rows: Row[], key: string, format: CellFormatter): string[] {
    const set = new Set<string>();
    for (const r of rows) set.add(format(key, r[key]));
    return [...set].sort((a, b) => {
        if (a === DASH) return 1;
        if (b === DASH) return -1;
        return a.localeCompare(b, 'pt-BR', { numeric: true });
    });
}

/** A busca olha todas as colunas mostradas (e o ID da conta, que nao aparece); coluna + valor exigem igualdade exata. */
export function filterRows(
    rows: Row[],
    columnKeys: string[],
    f: TableFilterState,
    format: CellFormatter,
    idKey = 'contaId',
): Row[] {
    const term = normalizeText(f.search);
    return rows.filter((r) => {
        if (f.column && f.value && format(f.column, r[f.column]) !== f.value) return false;
        if (!term) return true;
        const haystack = [r[idKey], ...columnKeys.map((k) => format(k, r[k]))]
            .map((v) => (v === null || v === undefined ? '' : String(v)))
            .join(' ');
        return normalizeText(haystack).includes(term);
    });
}

export type SortDir = 'asc' | 'desc';
export interface SortState {
    key: string;
    dir: SortDir;
}

/** Ordena por coluna: numeros e datas pelo valor cru, texto sem acento; vazio sempre por ultimo. */
export function sortRows(rows: Row[], sort: SortState | null, format: CellFormatter): Row[] {
    if (!sort) return rows;
    const { key, dir } = sort;
    const factor = dir === 'asc' ? 1 : -1;
    const isEmpty = (v: unknown) => v === null || v === undefined || v === '';
    return [...rows].sort((a, b) => {
        const va = a[key];
        const vb = b[key];
        if (isEmpty(va) && isEmpty(vb)) return 0;
        if (isEmpty(va)) return 1;
        if (isEmpty(vb)) return -1;
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * factor;
        if (typeof va === 'boolean' && typeof vb === 'boolean') return (Number(va) - Number(vb)) * factor;
        return normalizeText(format(key, va)).localeCompare(normalizeText(format(key, vb)), 'pt-BR', { numeric: true }) * factor;
    });
}

/** Clique no cabecalho: nova coluna abre crescente; a mesma alterna a direcao. */
export function nextSort(current: SortState | null, key: string): SortState {
    if (current && current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
    return { key, dir: 'asc' };
}
